import axios from 'axios';

/**
 * Resolves the API base URL at module load time using the following priority:
 *   1. `api_base_url` in localStorage  — set by LoginPage when the user selects a
 *      region/environment (dev | staging | prod × AU | US). Cleared on logout via
 *      TopBar → handleSwitchRegion().
 *   2. REACT_APP_API_URL build-time env var (optional override for CI/CD).
 *   3. Default: current hostname + port 8123 (works for both localhost and remote IP).
 *
 * NOTE: If a stale localStorage entry is present from a previous dev session,
 * all API calls will silently target that old URL. A console.warn is emitted
 * below so this is visible in browser DevTools. Clear it via:
 *   localStorage.removeItem('api_base_url')
 */
const getBaseUrl = () => {
  const storedUrl = localStorage.getItem('api_base_url');
  if (storedUrl) {
    console.warn(
      `[BIRT] Using API URL from localStorage: "${storedUrl}". ` +
      'If this is unexpected, clear it with: localStorage.removeItem("api_base_url")'
    );
    return storedUrl;
  }

  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Default to current hostname with port 8123
  // This supports both localhost and remote access (e.g. via IP)
  return `http://${window.location.hostname}:8123`;
};

const API_BASE_URL = getBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
});

/**
 * Updates the active API base URL for the current session.
 * Called by LoginPage when the user switches region or environment.
 * Persists to localStorage so the selection survives page refreshes.
 * Cleared on logout via TopBar → handleSwitchRegion().
 */
export const setApiBaseUrl = (url) => {
  api.defaults.baseURL = url;
  localStorage.setItem('api_base_url', url);
};

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: (username, password) =>
    api.post('/api/v1/auth/login', { username, password }),

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  setCurrentUser: (user, token) => {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', token);
  }
};

// Cemetery API
export const cemeteryAPI = {
  list: (config = {}) => api.get('/api/v1/cemeteries', config),
  get: (uniqueName) => api.get(`/api/v1/cemeteries/${uniqueName}`),
};

// Backup API
export const backupAPI = {
  create: (cemeteryName, backupTypes, excludeUniqueId = false) =>
    api.post('/api/v1/backup/create', {
      cemetery_unique_name: cemeteryName,
      backup_types: backupTypes,
      include_summary: true,
      exclude_unique_id: excludeUniqueId,
    }),

  createAsync: (cemeteryName, backupTypes, excludeUniqueId = false) =>
    api.post('/api/v1/backup/create-async', {
      cemetery_unique_name: cemeteryName,
      backup_types: backupTypes,
      include_summary: true,
      exclude_unique_id: excludeUniqueId,
    }),

  list: (cemeteryName = null) => {
    const params = cemeteryName ? { cemetery_name: cemeteryName } : {};
    return api.get('/api/v1/backup/list', { params });
  },

  download: (filename) =>
    api.get(`/api/v1/backup/download/${filename}`, {
      responseType: 'blob',
    }),

  delete: (filename) => api.delete(`/api/v1/backup/delete/${filename}`),

  getStatus: (taskId) => api.get(`/api/v1/backup/status/${taskId}`),

  getDownloadStatus: (taskId) => api.get(`/api/v1/backup/${taskId}/download`),

  getActiveTasks: () => api.get('/api/v1/backup/active-tasks'),

  cancel: (taskId) => api.post(`/api/v1/backup/cancel/${taskId}`),

  getHistory: (params = {}, config = {}) => api.get('/api/v1/backup/history', { params, ...config }),

  regenerateDownload: (backupId, expirationMinutes = 60) =>
    api.post(`/api/v1/backup/history/${backupId}/download`, { expiration_minutes: expirationMinutes }),
};

// Import API
export const importAPI = {
  upload: (cemeteryName, files) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    return api.post(`/api/v1/import/${cemeteryName}/upload`, formData);
  },

  start: (cemeteryName, importTypes, options = {}) =>
    api.post(`/api/v1/import/${cemeteryName}/start`, {
      import_types: importTypes,
      options,
      dry_run: false,
    }),

  getStatus: (cemeteryName, taskId) =>
    api.get(`/api/v1/import/${cemeteryName}/status/${taskId}`),

  cancel: (cemeteryName, taskId) =>
    api.post(`/api/v1/import/${cemeteryName}/cancel/${taskId}`),

  listFiles: (cemeteryName) =>
    api.get(`/api/v1/import/${cemeteryName}/files`),

  getFileContent: (cemeteryName, fileId) =>
    api.get(`/api/v1/import/${cemeteryName}/files/${fileId}/content`, { responseType: 'blob' }),

  deleteFile: (cemeteryName, fileId) =>
    api.delete(`/api/v1/import/${cemeteryName}/files/${fileId}`),

  deleteAllFiles: (cemeteryName) =>
    api.delete(`/api/v1/import/${cemeteryName}/files`),

  getActiveTasks: () => api.get('/api/v1/import/active-tasks'),

  wipeData: (cemeteryName, dataTypes) =>
    api.post(`/api/v1/import/${cemeteryName}/wipe`, {
      data_types: dataTypes,
    }),

  getStatistics: (cemeteryName, { refresh = false } = {}) =>
    api.get(`/api/v1/import/${cemeteryName}/statistics`, { params: refresh ? { refresh: true } : {} }),

  getHistory: (params = {}, config = {}) => api.get('/api/v1/import/history', { params, ...config }),

  compareBackup: (cemeteryName, { backupId = null, file = null, force = false } = {}) => {
    const formData = new FormData();
    if (backupId != null) formData.append('backup_id', backupId);
    if (file) formData.append('file', file);
    const params = force ? { force: true } : {};
    return api.post(`/api/v1/import/${cemeteryName}/compare-backup`, formData, { params });
  },

  // V4 Legacy Endpoints - Added for Parity work
  legacyImport: (cemeteryName, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/v1/import/${cemeteryName}/v4/import`, formData);
  },

  legacyBackup: (cemeteryName) =>
    api.post(`/api/v1/import/${cemeteryName}/v4/backup`, {}, {
      responseType: 'blob'
    }),
};

// Daily Backup API
export const dailyBackupAPI = {
  status: (envLabel = null) =>
    api.get('/api/v1/daily-backup/status', { params: envLabel ? { env_label: envLabel } : {} }),

  listRuns: (params = {}) =>
    api.get('/api/v1/daily-backup/runs', { params }),

  getRun: (runId) =>
    api.get(`/api/v1/daily-backup/runs/${runId}`),

  runNow: ({ envLabel = null, cemeteryNames = null } = {}) =>
    api.post('/api/v1/daily-backup/run-now', {
      env_label: envLabel,
      cemetery_names: cemeteryNames,
    }),

  cleanup: ({ beforeDate, envLabel = null, keepPerCemetery = null, confirm = false }) =>
    api.post('/api/v1/daily-backup/cleanup', {
      before_date: beforeDate,
      env_label: envLabel,
      keep_per_cemetery: keepPerCemetery,
      confirm,
    }),
};

// Wipe API
export const wipeAPI = {
  getHistory: (params = {}, config = {}) => api.get('/api/v1/import/wipe-history', { params, ...config }),
};

// Demo Auto-Reset API (TSP-3886)
export const demoResetAPI = {
  status: (cemeteryUniqueName = null) =>
    api.get('/api/v1/demo-reset/status', {
      params: cemeteryUniqueName ? { cemetery_unique_name: cemeteryUniqueName } : {},
    }),

  listRuns: (params = {}) => api.get('/api/v1/demo-reset/runs', { params }),

  getRun: (runId) => api.get(`/api/v1/demo-reset/runs/${runId}`),

  runNow: ({ cemeteryUniqueName = null, force = false } = {}) =>
    api.post('/api/v1/demo-reset/run-now', {
      cemetery_unique_name: cemeteryUniqueName,
      force,
    }),

  getDataset: (cemeteryUniqueName) =>
    api.get(`/api/v1/demo-reset/${cemeteryUniqueName}/dataset`),

  uploadDataset: (cemeteryUniqueName, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/v1/demo-reset/${cemeteryUniqueName}/dataset`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / e.total))
        : undefined,
    });
  },

  uploadDatasetFile: (cemeteryUniqueName, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/v1/demo-reset/${cemeteryUniqueName}/dataset/file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / e.total))
        : undefined,
    });
  },

  deleteDatasetFile: (cemeteryUniqueName, sourceId) =>
    api.delete(`/api/v1/demo-reset/${cemeteryUniqueName}/dataset/file/${sourceId}`),

  deleteAllDataset: (cemeteryUniqueName) =>
    api.delete(`/api/v1/demo-reset/${cemeteryUniqueName}/dataset`),
};

// Media Upload API (general-purpose R2 file uploads)
export const mediaUploadAPI = {
  listDirectories: (cemetery) =>
    api.get(`/api/v1/media-upload/${cemetery}/directories`),

  browseDirectories: (cemetery, path = '') =>
    api.get(`/api/v1/media-upload/${cemetery}/browse`, { params: { path } }),

  browseFiles: (cemetery, path = '') =>
    api.get(`/api/v1/media-upload/${cemetery}/browse/files`, { params: { path } }),

  uploadFile: (cemetery, path, file, onProgress, relativePath) => {
    const formData = new FormData();
    formData.append('path', path);
    formData.append('file', file);
    if (relativePath) {
      formData.append('relative_path', relativePath);
    }

    return api.post(`/api/v1/media-upload/${cemetery}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / e.total))
        : undefined,
    });
  },

  uploadFilesAsync: (cemetery, path, files, relativePaths) => {
    const formData = new FormData();
    formData.append('path', path);
    files.forEach((file) => formData.append('files', file));
    if (relativePaths && relativePaths.length > 0) {
      formData.append('relative_paths', JSON.stringify(relativePaths));
    }
    return api.post(`/api/v1/media-upload/${cemetery}/files/async`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  listSourceFiles: (cemetery) =>
    api.get(`/api/v1/media-upload/${cemetery}/source-files`),

  moveFromSource: (cemetery) =>
    api.post(`/api/v1/media-upload/${cemetery}/move-from-source`),

  getActiveMediaTasks: () =>
    api.get('/api/v1/media-upload/active-media-tasks'),
};

export default api;
